import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { OfficeEditFormValues, OfficePhoneRow } from "services/api/offices/types";
import { getOfficePhonesAPI, deleteOfficePhoneAPI } from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { ReactSVG } from "react-svg";
import { Loader } from "ui/components/Loader";
import { Dialog } from "ui/components/Dialog";
import OfficePhoneEditCard from "./OfficePhoneEditCard/OfficePhoneEditCard";
import { Badge } from "ui/components/Badge";

interface OfficePhoneStepProps {
  formValues: OfficeEditFormValues;
}

const OfficePhoneStep: React.FC<OfficePhoneStepProps> = ({ formValues: parentFormValues }) => {
  const officeId = parentFormValues.id;
  const { t } = useTranslate();
  const [phones, setPhones] = useState<OfficePhoneRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState<string | null>(null);
  const [deletePhoneId, setDeletePhoneId] = useState<string | null>(null);

  const fetchPhones = useCallback(async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const result = await getOfficePhonesAPI(initialDataWithPagination.pagination, officeId);
      setPhones(result.data);
    } catch {
      setPhones([]);
    }
    setIsLoading(false);
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchPhones();
  }, [officeId]);

  const handleEdit = (phoneId: string) => {
    setEditPhoneId(phoneId);
    setShowEdit(true);
  };

  const handleAddNew = () => {
    setEditPhoneId(null);
    setShowEdit(true);
  };

  const handleDelete = (phoneId: string) => {
    setDeletePhoneId(phoneId);
  };

  const handleDeleteConfirm = async () => {
    if (!officeId || !deletePhoneId) return;
    await deleteOfficePhoneAPI(t, officeId, deletePhoneId);
    setDeletePhoneId(null);
    fetchPhones();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditPhoneId(null);
    fetchPhones();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Telefoni poslovnice</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte telefonske brojeve poslovnice klikom na gumb <strong>"Dodaj telefon"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {phones.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj telefon"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  <div className="">
                    {phones.map((phone) => (
                      <div key={phone.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0">
                        <div>
                          <div className="text-sm font-medium flex items-center gap-2"><span>{phone.phoneNumber}</span>  {phone.primary && <Badge text="Primarni" variant="info" height="small" />}</div>
                          {phone.description && <div className="text-sm text-slate-500">{phone.description}</div>}
                        </div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => handleEdit(phone.id)} className="p-2 hover:bg-slate-200 rounded-full">
                            <ReactSVG src="/media/icons/edit.svg" className="w-4 h-4 stroke-slate-600" />
                          </button>
                          <button type="button" onClick={() => handleDelete(phone.id)} className="p-2 hover:bg-red-100 rounded-full">
                            <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj telefon" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <OfficePhoneEditCard
        officeId={officeId ?? ""}
        phoneId={editPhoneId}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deletePhoneId}
        title="Brisanje telefona"
        message="Jeste li sigurni da želite obrisati ovaj telefon?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletePhoneId(null)}
      />
    </>
  );
};

export default memo(OfficePhoneStep);
